import { useQuery } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  FulfillmentRiskResponse,
  RawFulfillmentRiskResponse,
  RawPick,
  RawRiskSection,
  RiskItem,
  RiskLevel,
  RiskSection,
} from './types'

/**
 * Flatten nested per-pick API rows into per-line RiskItems.
 * One pick with N lines becomes N rows, each carrying the pick-level
 * fields merged with one line's fields. Line-level `risk_level` wins
 * over pick-level so per-product severity renders accurately.
 *
 * The engine sorts picks by their *worst* line risk, but lines within a
 * pick render in pick-line order — so a pick whose worst line is AT_RISK
 * mixes its OK lines in next to its AT_RISK ones. After flattening we
 * re-sort by line-level severity, then by scheduled date, so all CRITICAL
 * lines land at the top, then ACTION_REQUIRED, then AT_RISK, etc.
 */
const RISK_ORDER: Record<RiskLevel, number> = {
  CRITICAL: 0,
  ACTION_REQUIRED: 1,
  AT_RISK: 2,
  WATCH: 3,
  OK: 4,
}

export function flattenPicksToRows(picks: RawPick[]): RiskItem[] {
  const rows: RiskItem[] = []
  for (const pick of picks) {
    const lines = pick.lines ?? []
    for (const line of lines) {
      rows.push({
        order_number: pick.so_number,
        order_id: pick.order_id,
        partner_id: pick.partner_id ?? 0,
        customer: pick.customer,
        scheduled_date: pick.scheduled_date,
        pick_number: pick.pick_number,
        product_id: line.product_id ?? 0,
        product_tmpl_id: line.product_tmpl_id ?? null,
        product: line.product,
        uom: line.uom ?? '',
        needed: line.needed,
        reserved: line.reserved,
        shortfall: line.shortfall,
        on_hand: line.on_hand,
        available_for_pick: line.available_for_pick ?? line.on_hand ?? 0,
        incoming: line.incoming,
        forecast: line.forecast,
        risk_level: line.risk_level,
        reason: line.reason,
        incoming_pos: line.incoming_pos,
      })
    }
  }
  rows.sort((a, b) => {
    const ra = RISK_ORDER[a.risk_level] ?? 99
    const rb = RISK_ORDER[b.risk_level] ?? 99
    if (ra !== rb) return ra - rb
    const da = a.scheduled_date ?? ''
    const db = b.scheduled_date ?? ''
    return da.localeCompare(db)
  })
  return rows
}

function flattenSection(raw: RawRiskSection): RiskSection {
  return {
    items: flattenPicksToRows(raw.items),
    summary: raw.summary,
    broken_procurement: raw.broken_procurement,
    stale_picks: raw.stale_picks ? flattenPicksToRows(raw.stale_picks) : undefined,
  }
}

export function useFulfillmentRisk() {
  return useQuery({
    queryKey: ['fulfillment-risk', 'list'],
    queryFn: async (): Promise<FulfillmentRiskResponse> => {
      const raw = await jsonFetch<RawFulfillmentRiskResponse>('/api/fulfillment/risk')
      return {
        commercial: flattenSection(raw.commercial),
        samples: flattenSection(raw.samples),
        generated_at: raw.generated_at,
        odoo_pick_count: raw.odoo_pick_count,
        algorithm_version: raw.algorithm_version,
      }
    },
    staleTime: 60_000,
    refetchInterval: 180_000,
  })
}
